import React, { useEffect, useState } from "react";
import Container from "../../common/components/Container/Container";
import Breadcrumb from "../../common/components/Breadcrumb/Breadcrumb";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import i18n from "../../internationalization/i18n";
import { Button, Modal } from "antd";
import {
	RightCircleOutlined,
	CloseOutlined
} from "@ant-design/icons";
import {
	useGetVehicleInfoQuery,
	useGetVehicleTransactionsQuery,
  useStartVehicleTransactionMutation,
  useStopVehicleTransactionMutation,
  fleetApi
} from "services/fleetService";
import { useDispatch, useSelector } from "react-redux";
import { rootReducerType } from "../../redux/combineReducers";
import useWindowDimensions, { counterCommonItemsArrays, decodeJWT, getDataToDisplay } from "../../common/functions";
import InfoSection from "./components/InfoSection/InfoSection";
import "./Fleet.less";
import FleetTransactionsTable from "./components/fleetTransactions/FleetTransactionsTable";
import { capitalizeFirstLetter, emailToUsername } from "common/utils";
import Notification from "common/components/NotificationMessage/Notification";
import { FleetTransactionItem } from "services/utils/interfaces";

type FleetParams = {
	vehicleId: string;
};

interface ILocation {
	locationIdStationId: string;
	status: string;
}

export type FleetTransactions = Array<FleetTransactionItem>;

const Fleet: React.FC<{ shouldRefresh: boolean }> = ({ shouldRefresh }): React.ReactElement => {
	const navigate = useNavigate();
	const location = useLocation();

	const { width } = useWindowDimensions();

	const { vehicleId } = useParams<keyof FleetParams>() as FleetParams;

	const { status } = location.state as ILocation;
	const { isLoggedIn } = useSelector((state: rootReducerType) => state.authenticationReducer);

  const [startCharger, { isSuccess: startSuccess, data: startData }] = useStartVehicleTransactionMutation();
	const [stopCharger, { isSuccess: stopSuccess, data: stopData }] = useStopVehicleTransactionMutation();

	const [limit] = useState<number>(10);
	const [offset, setOffset] = useState<number>(0);
	const [previousOffset, setPreviousOffset] = useState<number>(offset);

	const [fleetTransactions, setFleetTransactions] = useState<FleetTransactions>([]);
  
	const {data: vehicleInfo, refetch: refetchFleetInfo} = useGetVehicleInfoQuery({
		userName: decodeJWT(isLoggedIn).email,
		vehicleId,
	});

	const { data, refetch: refetchFleetTransactions } = useGetVehicleTransactionsQuery({
		userName: decodeJWT(isLoggedIn).email,
		vehicleId,
		limit: String(limit),
		offset: String(offset < 0 ? 0 : offset),
	})

	const dispatch = useDispatch();

	useEffect(() => {
		if (offset < previousOffset) return;
		if (offset === previousOffset) {
			const tempOffset = offset < 0 ? 0 : offset;
			setOffset(tempOffset);
			setPreviousOffset(tempOffset);

			if (vehicleId) {
				dispatch(
					fleetApi.endpoints.getVehicleTransactions.initiate(
						{
							userName: decodeJWT(isLoggedIn).email,
							limit: String(limit),
							offset: String(tempOffset),
							vehicleId
						},
						{ subscribe: false, forceRefetch: true }
					)
				);
			}
		}
	}, [offset]);


	useEffect(() => {
		if (!data?.items) return;

		const tempOffset = offset < 0 ? 0 : offset;
		setOffset(tempOffset);
		setPreviousOffset(tempOffset);

		const counterCommonItems = counterCommonItemsArrays(data.items, fleetTransactions);

		const newItems = JSON.parse(JSON.stringify(
			offset <= 0
				? [...data.items]
				: (counterCommonItems === 0
					? [...fleetTransactions, ...data.items]
					: [...fleetTransactions]
				)));
		setFleetTransactions(newItems);
		setPreviousOffset(offset);
	}, [JSON.stringify(data?.items), offset]);

	useEffect(() => {
		if (vehicleId) {
			refetchFleetInfo();
			refetchFleetTransactions();
		}
	}, [shouldRefresh]);
	const userName = decodeJWT(isLoggedIn).email;

	const breadcrumbItems = [
		{ label: i18n.t("breadcrumbs.fleet.fleets"), route: "/fleets" },
		{ label: vehicleId || "" },
	];

	const executeFunction = (action: string) => {
		if (!vehicleId) {
			return null;
		}

		switch (action) {
			case "start": {
				startCharger({
					userName: emailToUsername(userName),
					vehicleId
				});
				break;
			}
			case "stop": {
				stopCharger({
					userName: emailToUsername(userName),
					vehicleId
				});
				break;
			}
			default: break;
		}
	};

	const popUpModal = (action: string) => {

		const content = <div className="custom-content">
			{
				action === "start" ? <RightCircleOutlined style={{ color: "green", transform: "scale(2.5)" }} /> :
					action === "stop" ? <CloseOutlined style={{ color: "red", transform: "scale(2.5)" }} /> :
						null
			}
			<div className="content-title">{i18n.t("fleet.popUpTitle", { action: action }).charAt(0).toUpperCase() + i18n.t("fleet.popUpTitle", { action: action }).slice(1)}</div>
			<div className="content-description">{i18n.t("fleet.popUpMessage", { action: action })}</div>
		</div>;

		Modal.confirm({
			className: "customModal",
			content: content,
			centered: true,
			okText: i18n.t("constants.confirm"),
			onOk: () => executeFunction(action),
			cancelText: i18n.t("constants.cancel")
		});
	};

	useEffect(() => {
		if (startSuccess)
			Notification({
				type: startData?.success ? "success" : "warning",
				message: i18n.t(startData?.success ? "fleet.successModalTitle" : "fleet.warningModalTitle"),
				description: startData?.success ? i18n.t("fleet.successModalDesc", { action: "start" }) : capitalizeFirstLetter(startData?.message || "")
			});
	}, [startSuccess]);

	useEffect(() => {
		if (stopSuccess)
			Notification({
				type: stopData?.success ? "success" : "warning",
				message: i18n.t(stopData?.success ? "fleet.successModalTitle" : "fleet.warningModalTitle"),
				description: stopData?.success ? i18n.t("fleet.successModalDesc", { action: "stop" }) : capitalizeFirstLetter(stopData?.message || "")
			});
	}, [stopSuccess]);

	return width > 768 ? (
		<Container
			backButtonAction={() => navigate("/fleets")}
			styleNoFlex={true}
			loadingPageName={`${i18n.t("fleet.fleet")} ${vehicleId}`}
			subTitle={<Breadcrumb items={breadcrumbItems} />}
			actionsRight={<div style={{ marginTop: "16px" }}>
				<Button className="fleet-button" onClick={() => popUpModal("start")}>
					<RightCircleOutlined style={{ color: "green" }} />{i18n.t("fleet.startFleet")}
				</Button>
				<Button className="fleet-button" onClick={() => popUpModal("stop")}>
					<CloseOutlined style={{ color: "red" }} />{i18n.t("fleet.stopFleet")}
				</Button>
			</div>}
		>
			<div style={{ minHeight: "100%", height: "auto", width: "93vw", padding: "0 20px", margin: "10px auto 0", background: '#fff',
				paddingTop: '0px' }}>
				{
					vehicleInfo && vehicleId && status ? (
						<section className="fleet-container-border">
							<InfoSection
								data={vehicleInfo}
								shouldRefresh={shouldRefresh}
								fleetId={vehicleId}
								status={status} />
						</section>
					)
						: null
				}
				{
					fleetTransactions && vehicleId && status ? (
						<div className="fleet-transactions-table" >
							<FleetTransactionsTable
								limit={limit}
								offset={offset < 0 ? 0 : offset}
								setOffset={setOffset}
								length={fleetTransactions.length + ((data?.items.length || 0) < limit ? 0 : 1)}
								data={getDataToDisplay(fleetTransactions, limit, offset)}
							/>
						</div>
					)
						: null
				}
			</div>
		</Container>) : (
		<Container
			backButtonAction={() => navigate("/fleets")}
			loadingPageName={`Fleet ${vehicleId}`}
			subTitle={<Breadcrumb items={breadcrumbItems} />}
		>
			<div className="fleet">
				<Button className="fleet-button" onClick={() => popUpModal("start")}>
					<RightCircleOutlined style={{ color: "green" }} />{i18n.t("fleet.startFleet")}
				</Button>
				<Button className="fleet-button" onClick={() => popUpModal("stop")}>
					<CloseOutlined style={{ color: "red" }} />{i18n.t("fleet.stopFleet")}
				</Button>
				{
					vehicleInfo && vehicleId && status ? (
						<div className="fleet-container-border">
							<InfoSection
								data={vehicleInfo}
								shouldRefresh={shouldRefresh}
								fleetId={vehicleId}
								status={status} />
						</div>
					)
						: null
				}
				<div style={{ height: "100%", width: "90vw", marginTop: "-10px" }}>
					{
						fleetTransactions && vehicleId && status ? (
							<div className="fleet-transactions-table">
								<FleetTransactionsTable
									limit={limit}
									offset={offset < 0 ? 0 : offset}
									setOffset={setOffset}
									length={fleetTransactions.length + ((data?.items.length || 0) < limit ? 0 : 1)}
									data={getDataToDisplay(fleetTransactions, limit, offset)}
								/>
							</div>
						)
						: null
					}
				</div>
			</div>
		</Container>
	);
};

export default Fleet;
